import {
  getStudentProgressLabel,
  isRunStep,
  shouldShowStudentRunLoader
} from '@/lib/stepVisibility';
import { MessageContext } from 'contexts/MessageContext';
import React, { memo, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type IAction,
  type IMessageElement,
  type IStep
} from '@chainlit/react-client';

import FonLoadingIndicator from './FonLoadingIndicator';
import { Message } from './Message';

interface Props {
  messages: IStep[];
  elements: IMessageElement[];
  actions: IAction[];
  indent: number;
  isRunning?: boolean;
  scorableRun?: IStep;
}

const Messages = memo(
  ({ messages, elements, actions, indent, isRunning, scorableRun }: Props) => {
    const messageContext = useContext(MessageContext);
    const { i18n } = useTranslation();
    return (
      <>
        {messages.map((m) => {
          // Handle chainlit runs
          if (isRunStep(m)) {
            const isRunning = !m.end && !m.isError && messageContext.loading;
            const showRunLoader = shouldShowStudentRunLoader(
              m,
              messageContext.loading
            );
            // Ignore on_chat_start for scorable run
            const scorableRun =
              !isRunning && m.name !== 'on_chat_start' ? m : undefined;
            return (
              <React.Fragment key={m.id}>
                {m.steps?.length ? (
                  <Messages
                    messages={m.steps}
                    elements={elements}
                    actions={actions}
                    indent={indent}
                    isRunning={isRunning}
                    scorableRun={scorableRun}
                  />
                ) : null}
                {showRunLoader && m.name !== 'on_chat_start' ? (
                  <FonLoadingIndicator
                    label={getStudentProgressLabel(m, i18n.language)}
                  />
                ) : null}
              </React.Fragment>
            );
          } else {
            // Score the current run
            const _scorableRun = m.type === 'run' ? m : scorableRun;
            // The message is scorable if it is the last assistant message of the run

            const isRunLastAssistantMessage =
              m ===
              _scorableRun?.steps?.findLast(
                (_m) => _m.type === 'assistant_message'
              );

            const isLastAssistantMessage =
              messages.findLast((_m) => _m.type === 'assistant_message') === m;

            const isScorable =
              isRunLastAssistantMessage || isLastAssistantMessage;

            return (
              <Message
                message={m}
                elements={elements}
                actions={actions}
                key={m.id}
                indent={indent}
                isRunning={isRunning}
                scorableRun={_scorableRun}
                isScorable={isScorable}
              />
            );
          }
        })}
      </>
    );
  }
);

export { Messages };
